import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { RefreshCw, X } from "lucide-react";
import { performFullRefresh } from "./ForceRefreshButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POLL_INTERVAL_MS = 60_000;
const STORAGE_KEY = "app_version";
const DISMISSED_KEY = "app_version_dismissed_for";

/**
 * Polls /api/app-version every minute. When the server's `current_version`
 * exceeds the version stored locally on first run, shows a sticky banner that
 * invites the user to refresh. Users can dismiss the banner per-version.
 */
export default function VersionWatcher() {
  const [serverVersion, setServerVersion] = useState(null);
  const [message, setMessage] = useState("");
  const [dismissed, setDismissed] = useState(false);
  const initialized = useRef(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    let timer;

    const check = async () => {
      try {
        const res = await axios.get(`${API}/app-version`, { timeout: 5000 });
        const v = Number(res?.data?.current_version || 0);
        const msg = res?.data?.message || "";
        if (!v) return;

        const stored = Number(localStorage.getItem(STORAGE_KEY) || 0);
        if (!stored) {
          // First time we ever check — pin baseline so we only prompt on FUTURE bumps
          localStorage.setItem(STORAGE_KEY, String(v));
          initialized.current = true;
          return;
        }

        if (v > stored) {
          const dismissedFor = Number(localStorage.getItem(DISMISSED_KEY) || 0);
          setServerVersion(v);
          setMessage(msg);
          setDismissed(dismissedFor === v);
        }
      } catch (e) {
        // Silent — polling failure shouldn't disrupt the app
      }
    };

    check();
    timer = setInterval(check, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    // performFullRefresh already updates app_version in localStorage before reload
    await performFullRefresh();
  };

  const handleDismiss = () => {
    if (serverVersion) {
      localStorage.setItem(DISMISSED_KEY, String(serverVersion));
    }
    setDismissed(true);
  };

  if (!serverVersion || dismissed) return null;

  return (
    <div
      data-testid="version-update-banner"
      className="fixed top-0 left-0 right-0 z-[1000] bg-amber-500 text-slate-900 shadow-lg"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <RefreshCw className="w-4 h-4 flex-shrink-0" />
          <p className="text-sm font-medium truncate">
            {message
              ? `A new version of bohhub.com is available — ${message}`
              : "A new version of bohhub.com is available."}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="version-banner-refresh-button"
            className="bg-slate-900 text-white text-xs sm:text-sm px-3 py-1.5 rounded-md hover:bg-slate-800 disabled:opacity-60"
          >
            {refreshing ? "Refreshing…" : "Refresh now"}
          </button>
          <button
            onClick={handleDismiss}
            data-testid="version-banner-dismiss-button"
            aria-label="Dismiss"
            className="p-1 rounded hover:bg-amber-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
